import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { FooterComponent } from '../../../shared/components/footer/footer.component';
import { AssetHeaderComponent, AssetHeaderConfig } from '../../../shared/components/asset-header/asset-header.component';
import { CreateDiscussionFormComponent } from '../../../shared/components/create-discussion-form/create-discussion-form.component';
import { DiscussionFormData } from '../../../shared/components/create-discussion-offcanvas/create-discussion-offcanvas.component';
import { DiscussionApiService, FileApiService } from '@angular-workspace/services';
import { LoginModalComponent } from '../../../shared/components/login-modal/login-modal.component';
import { ConfirmationDialogComponent } from '../../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { DiscussionSidebarComponent } from '../discussion-sidebar/discussion-sidebar.component';
import { ToasterService } from '../../../services/toaster.service';
import { GoogleAnalyticsService } from '../../../services/google-analytics.service';
import { KeycloakService } from 'keycloak-angular';
import { isDiscussionRejectedStatus } from '../shared/discussion-status.util';

/** Reverse map: API category (DATA_BANKS, etc.) -> form assetCategory (Dataset, etc.) */
const categoryToAssetCategory: { [key: string]: string } = {
  'DATA_BANKS': 'Dataset',
  'AI_MODELS': 'Model',
  'USE_CASES': 'Use Case',
  'OTHERS': 'Others'
};

@Component({
  selector: 'app-discussion-create',
  standalone: true,
  imports: [
    CommonModule,
    HeaderComponent,
    FooterComponent,
    AssetHeaderComponent,
    CreateDiscussionFormComponent,
    LoginModalComponent,
    DiscussionSidebarComponent,
    ConfirmationDialogComponent,
  ],
  templateUrl: './create.component.html',
  styleUrl: './create.component.scss',
})
export class DiscussionCreateComponent implements OnInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private discussionApiService = inject(DiscussionApiService);
  private fileApiService = inject(FileApiService);
  private toasterService = inject(ToasterService);
  private googleAnalytics = inject(GoogleAnalyticsService);
  private keycloak = inject(KeycloakService);

  discussionInitialValues: Partial<DiscussionFormData> = {};
  editDiscussionId: string | null = null;
  /** Original tags when editing - used to compute add/remove for edit API */
  editOriginalTags: string[] = [];
  isLoggedIn: boolean = false;
  showLoginModal: boolean = false;
  showDiscussionSubmittedDialog = false;
  private discussionSubmittedAckPending = false;
  readonly discussionSubmittedDialogTitle = 'Request submitted';
  readonly discussionSubmittedDialogMessage =
    'Your discussion request has been sent to the platform admin for approval.';
  isLoadingEdit: boolean = false;
  activeTab: string = ''; // Empty string to prevent highlighting on create page
  /** Latest admin review comment when editing */
  adminComment: string | null = null;
  /** True when logged-in user owns the discussion being edited */
  isEditDiscussionOwner: boolean = false;

  headerConfig: AssetHeaderConfig = {
    breadcrumbs: [
      { label: 'Home', route: '/' },
      { label: 'Discussions', route: '/discussions' },
      { label: 'Create Discussion' }
    ],
    title: 'Create Discussion',
    description: 'Start a new discussion to share insights and collaborate with the community'
  };

  async ngOnInit(): Promise<void> {
    await this.checkLoginStatus();
    if (!this.isLoggedIn) {
      this.showLoginModal = true;
      return;
    }

    const snapshot = this.route.snapshot.queryParams;

    // Edit mode: fetch discussion by ID and populate form
    const editId = snapshot['edit'];
    if (editId && typeof editId === 'string') {
      this.editDiscussionId = editId;
      this.headerConfig = {
        ...this.headerConfig,
        breadcrumbs: [
          { label: 'Home', route: '/' },
          { label: 'Discussions', route: '/discussions' },
          { label: 'Edit Discussion' }
        ],
        title: 'Edit Discussion',
        description: 'Update your discussion'
      };
      this.loadDiscussionForEdit(editId);
    } else {
      this.applyUrlParams(snapshot);
    }

    this.route.queryParams.subscribe(params => {
      if (params['edit']) return; // Handled in edit flow
      this.applyUrlParams(params);
    });
  }

  private applyUrlParams(params: { [key: string]: any }): void {
    if (params['category']) {
      this.discussionInitialValues = {
        ...this.discussionInitialValues,
        category: params['category']
      };
    }
    if (params['tab']) {
      this.activeTab = params['tab'];
    }
  }

  private async checkLoginStatus(): Promise<void> {
    try {
      this.isLoggedIn = await this.keycloak.isLoggedIn();
    } catch (error) {
      console.error('Error checking login status:', error);
      this.isLoggedIn = false;
    }
  }

  private async loadDiscussionForEdit(id: string): Promise<void> {
    this.isLoadingEdit = true;
    this.adminComment = null;
    this.isEditDiscussionOwner = false;
    try {
      const response = await this.discussionApiService.getReview(id);
      const data = response?.data ?? response;
      if (!data) {
        this.toasterService.error('Discussion not found');
        this.router.navigate(['/discussions']);
        return;
      }

      if (isDiscussionRejectedStatus(data)) {
        this.toasterService.error(
          'This discussion has been rejected and cannot be edited.',
        );
        this.router.navigate(['/discussions', id]);
        return;
      }

      this.adminComment = this.extractLatestAdminComment(data.discussion_reviews);
      const sub = (this.keycloak.getKeycloakInstance() as any)?.tokenParsed
        ?.sub as string | undefined;
      const ownerId = data.user?.id ?? data.user_id ?? data.owner_id;
      this.isEditDiscussionOwner =
        !!sub &&
        ownerId != null &&
        String(sub) === String(ownerId);

      const tags: string[] = (data.discussion_tags || [])
        .map((dt: any) => dt?.tag?.name)
        .filter((n: string | undefined) => n);
      this.editOriginalTags = [...tags];

      const rawCategory = data.category || data.type || '';
      const apiCategory = String(rawCategory).toUpperCase().replace(/[\s-]/g, '_');
      const assetCategory = categoryToAssetCategory[apiCategory] ?? 'Others';

      const existingAttachments = (data.discussion_attachments || [])
        .map((a: any) => {
          const meta = a.attachment_metadata;
          const key = meta?.s3_key ?? meta?.['s3 key'] ?? a.object_key ?? a.objectKey ?? a.id ?? a.attachment_id ?? '';
          const name = meta?.file_name ?? a.file_name ?? a.name ?? a.attachment?.file_name ?? a.attachment?.name ?? 'Attachment';
          return { key, name };
        })
        .filter((x: { key: string }) => x.key);

      this.discussionInitialValues = {
        category: data.type || data.category || '',
        title: data.title || '',
        assetCategory,
        assetId: data.sub_category_id ?? data.subCategoryId ?? '',
        assetName: data.sub_category ?? data.sub_category_name ?? '',
        content: data.content || data.description || '',
        tags: tags.length ? tags : [],
        existingAttachments: existingAttachments.length ? existingAttachments : undefined
      };
    } catch (error) {
      console.error('Error loading discussion for edit:', error);
      this.toasterService.error('Failed to load discussion');
      this.router.navigate(['/discussions']);
    } finally {
      this.isLoadingEdit = false;
    }
  }

  async onDiscussionPublish(formData: DiscussionFormData): Promise<void> {
    try {
      console.log('onDiscussionPublish called with formData:', formData);
      console.log('Form data details:', {
        assetCategory: formData.assetCategory,
        assetId: formData.assetId,
        assetName: formData.assetName,
        assetIdType: typeof formData.assetId,
        assetNameType: typeof formData.assetName,
        assetIdLength: formData.assetId?.length,
        assetNameLength: formData.assetName?.length
      });
      
      // Attachments are already uploaded and keys are passed from the form component
      const attachmentKeys: string[] = [];
      
      if (formData.attachments && formData.attachments.length > 0) {
        // Attachments should already be keys (strings) from the form component
        if (typeof formData.attachments[0] === 'string') {
          attachmentKeys.push(...(formData.attachments as string[]));
        } else {
          // Fallback: if somehow File objects are passed, log a warning
          console.warn('Unexpected attachment format. Expected string keys, got:', formData.attachments);
        }
      }

      // Map assetCategory to category (API expects category like "DATA_BANKS")
      const categoryMap: { [key: string]: string } = {
        'Dataset': 'DATA_BANKS',
        'Model': 'AI_MODELS',
        'Usecase': 'USE_CASES',
        'Use Case': 'USE_CASES',
        'Others': 'OTHERS'
      };

      // Prepare API payload
      const payload: any = {
        title: formData.title,
        type: formData.category, // e.g., "PUBLIC", "GENERAL", etc.
        category: categoryMap[formData.assetCategory] || 'OTHERS',
        content: formData.content,
        tags: formData.tags || [],
        attachments: attachmentKeys
      };

      // Add sub_category and sub_category_id only if they have values
      // Check for both empty string and undefined/null
      if (formData.assetName && formData.assetName.trim()) {
        payload.sub_category = formData.assetName.trim();
      }
      if (formData.assetId && formData.assetId.trim()) {
        payload.sub_category_id = formData.assetId.trim();
      }

      console.log('Final payload before API call:', payload);

      if (this.editDiscussionId) {
        const currentTags = formData.tags || [];
        const originalTags = this.editOriginalTags;
        const tagsToAdd = currentTags.filter(
          (t) => !originalTags.some((o) => o.toLowerCase() === t.toLowerCase())
        );
        const tagsToRemove = originalTags.filter(
          (o) => !currentTags.some((t) => t.toLowerCase() === o.toLowerCase())
        );

        const originalAttachmentKeys =
          this.discussionInitialValues.existingAttachments?.map((a) => a.key) || [];
        const addedAttachments = attachmentKeys.filter(
          (k) => !originalAttachmentKeys.includes(k)
        );

        const editPayload = {
          title: formData.title,
          category: categoryMap[formData.assetCategory] || 'OTHERS',
          sub_category: formData.assetName?.trim() || undefined,
          sub_category_id: formData.assetId?.trim() || undefined,
          content: formData.content,
          added_attachments: addedAttachments,
          tags: {
            add: tagsToAdd,
            remove: tagsToRemove
          }
        };

        const response = await this.discussionApiService.editDiscussion(
          this.editDiscussionId,
          editPayload
        );
        console.log('Discussion updated successfully:', response);
        this.toasterService.success('Discussion updated successfully.', 3000);
        this.router.navigate(['/discussions', this.editDiscussionId]);
      } else {
        const response = await this.discussionApiService.newDiscussion(payload);
        console.log('Discussion published successfully:', response);
        this.googleAnalytics.trackDiscussionCreate();
        this.openDiscussionSubmittedDialog();
      }
    } catch (error: any) {
      console.error('Error publishing discussion:', error);
      
      // Extract error message from error response
      let errorMessage = 'Failed to publish discussion. Please try again.';
      
      if (error?.error) {
        // Handle error object structure
        const errorObj = error.error;
        
        if (errorObj?.message) {
          errorMessage = errorObj.message;
        } else if (errorObj?.error?.details) {
          errorMessage = errorObj.error.details;
        } else if (errorObj?.error?.message) {
          errorMessage = errorObj.error.message;
        } else if (typeof errorObj === 'string') {
          errorMessage = errorObj;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      // Display error in toaster
      this.toasterService.error(errorMessage, 5000);
    }
  }

  private openDiscussionSubmittedDialog(): void {
    this.discussionSubmittedAckPending = true;
    this.showDiscussionSubmittedDialog = true;
  }

  onDiscussionSubmittedAcknowledge(): void {
    if (!this.discussionSubmittedAckPending) return;
    this.discussionSubmittedAckPending = false;
    this.showDiscussionSubmittedDialog = false;
    this.router.navigate(['/discussions']);
  }

  onDiscussionCancel(): void {
    this.router.navigate(['/discussions']);
  }

  /** Latest non-empty admin comment from discussion_reviews (newest by created_at). */
  private extractLatestAdminComment(reviews: unknown): string | null {
    if (!Array.isArray(reviews) || reviews.length === 0) return null;
    const sorted = [...reviews].sort((a: any, b: any) => {
      const ta = new Date(a?.created_at ?? 0).getTime();
      const tb = new Date(b?.created_at ?? 0).getTime();
      return tb - ta;
    });
    const latest = sorted[0];
    const text =
      typeof latest?.comment === 'string' ? latest.comment.trim() : '';
    return text.length ? text : null;
  }

  async createNewDiscussion(): Promise<void> {
    await this.checkLoginStatus();
    if (!this.isLoggedIn) {
      this.showLoginModal = true;
      return;
    }

    // Navigate to create discussion page with current category and tab
    const queryParams: any = {};
    if (this.discussionInitialValues.category) {
      queryParams.category = this.discussionInitialValues.category;
    }
    if (this.activeTab) {
      queryParams.tab = this.activeTab;
    }
    this.router.navigate(['/discussions/create-new'], { queryParams });
  }
}
